import { z } from 'zod';
import { UnixTimestamp } from '../types/branded';
import {
  AssistantPromptPayloadSchema,
} from './assistant';
import {
  NotificationCategory,
  NotificationDeliveryState,
  NotificationPriority,
  NotificationSource,
} from '../types/enums';

/**
 * Shared school-persona notification feed key.
 * Used as the organization-level stream identifier for school-side notification widgets.
 */
export const SCHOOL_NOTIFICATION_STREAM_KEY = 'school-persona-feed';

export const NotificationTargetRefSchema = z.object({
  module: z.string(),
  route: z.string(),
  aggregateRoot: z.string().nullable().optional(),
  entityId: z.string().nullable().optional(),
  familyId: z.string().nullable().optional(),
  applicationId: z.string().nullable().optional(),
});
export type NotificationTargetRef = z.infer<typeof NotificationTargetRefSchema>;

export const NotificationActorStateSchema = z.object({
  actorId: z.string(),
  role: z.string(),
  unreadCount: z.number().int().nonnegative().default(0),
  lastReadAt: UnixTimestamp.nullable().optional(),
});
export type NotificationActorState = z.infer<typeof NotificationActorStateSchema>;

export const NotificationItemSchema = z.object({
  id: z.string(),
  source: NotificationSource,
  category: NotificationCategory,
  priority: NotificationPriority,
  deliveryState: NotificationDeliveryState,
  title: z.string(),
  body: z.string().nullable().optional(),
  createdAt: UnixTimestamp,
  readAt: UnixTimestamp.nullable().optional(),
  module: z.string(),
  route: z.string(),
  sourceRef: z.string().nullable().optional(),
  traceId: z.string().nullable().optional(),
  assistantPrompt: AssistantPromptPayloadSchema.nullable().optional(),
  targetRef: NotificationTargetRefSchema.nullable().optional(),
  actorState: NotificationActorStateSchema.nullable().optional(),
});
export type NotificationItem = z.infer<typeof NotificationItemSchema>;

export const NotificationPreferenceSchema = z.object({
  actorId: z.string(),
  role: z.string(),
  moduleFilters: z.array(z.string()).default([]),
  categoryFilters: z.array(NotificationCategory).default([]),
  muted: z.boolean().default(false),
  digestFrequency: z.enum(['off', 'instant', 'daily', 'weekly']).default('instant'),
  channelPreferences: z.object({
    email: z.object({
      enabled: z.boolean().default(true),
      digestFrequency: z.enum(['off', 'instant', 'daily', 'weekly']).optional(),
    }).partial().optional(),
    sms: z.object({
      enabled: z.boolean().default(false),
      digestFrequency: z.enum(['off', 'instant', 'daily', 'weekly']).optional(),
    }).partial().optional(),
    push: z.object({
      enabled: z.boolean().default(false),
      digestFrequency: z.enum(['off', 'instant', 'daily', 'weekly']).optional(),
    }).partial().optional(),
    slack: z.object({
      enabled: z.boolean().default(false),
      digestFrequency: z.enum(['off', 'instant', 'daily', 'weekly']).optional(),
    }).partial().optional(),
  }).partial().default({}),
  updatedAt: UnixTimestamp,
});
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;
